import { Controller } from "@hotwired/stimulus";

// Recurring Potential: pick a plan (or override), see what MRR would
// look like if non-recurring members were on it. No server math, no
// hidden amortization assumptions — all multiplication happens here
// from values the operator entered.
export default class extends Controller {
  static targets = [
    "planSelect", "overridePrice", "overrideInterval",
    "potentialTotal", "totalMrr",
    "recurringToggle", "freeRecurringToggle", "prepaidToggle", "creditPackToggle", "uncategorizedToggle",
    "recurringTotal", "freeRecurringTotal", "prepaidTotal", "creditPackTotal", "uncategorizedTotal",
    "grandTotal"
  ];

  // `plans` is the full server-rendered catalog as JSON. Picking a plan
  // looks it up by id and populates the override fields with that plan's
  // native price + interval. We can't ferry the data on each <option>
  // element because Forms::SelectComponent flattens options to [label, value]
  // and drops per-option data attributes.
  static values = {
    currentMrrCents:      Number,
    recurringCount:       Number,
    freeRecurringCount:   Number,
    prepaidCount:         Number,
    creditPackCount:      Number,
    uncategorizedCount:   Number,
    plans:                Array
  };

  connect() {
    this.applyPlanDefaults();
    this.recompute();
  }

  // When the operator picks a plan from the dropdown, find it in the
  // catalog and write its native price + interval into the override
  // fields. They stay editable — the math always reads the overrides.
  applyPlanDefaults() {
    const select = this.planSelectTarget;
    if (!select) return;
    const selectedId = parseInt(select.value, 10);
    if (!selectedId) return;

    const plan = (this.hasPlansValue ? this.plansValue : []).find(p => p.id === selectedId);
    if (!plan) return;

    if (this.hasOverridePriceTarget) {
      this.overridePriceTarget.value = (plan.price_in_cents / 100).toFixed(2);
    }
    if (this.hasOverrideIntervalTarget) {
      // Only day/week/month are valid override intervals — year plans
      // aren't surfaced in the catalog at all.
      this.overrideIntervalTarget.value = plan.interval_type;
    }
  }

  recompute(event) {
    if (event && event.target === this.planSelectTarget) {
      this.applyPlanDefaults();
    }

    const monthlyCents = this.computeMonthlyCents();

    // Per-cohort toggles: default ON if the row isn't rendered (e.g. Free
    // / Comp row is hidden when count is 0) we still treat it as "on" so
    // a missing checkbox doesn't quietly turn anything off.
    const cohortOn = (name) => {
      const targetProp = `${name}ToggleTarget`;
      const hasProp = `has${name.charAt(0).toUpperCase()}${name.slice(1)}ToggleTarget`;
      if (!this[hasProp]) return true;
      return this[targetProp].checked;
    };

    // Per-cohort contributions to the scenario:
    //   - Paid recurring: swap their existing price for the new plan.
    //     Contribution = paidRecurringCount × monthly − current MRR.
    //     Can be NEGATIVE if the picked plan is cheaper than their average.
    //   - Free / comp recurring (stripe_status = "free"): currently $0 to
    //     monthly MRR, contribution = freeRecurringCount × monthly (gain only).
    //   - PIFs / Credit packs / Other: currently $0 monthly, contribution
    //     is pure gain = count × monthly.
    //
    // Each cohort row has its own toggle (default ON). Unticking excludes
    // that cohort from both its own row's value AND the aggregate / total.
    const freeRecurring      = this.hasFreeRecurringCountValue ? this.freeRecurringCountValue : 0;
    const paidRecurring      = Math.max(0, this.recurringCountValue - freeRecurring);
    const recurringDelta     = (monthlyCents * paidRecurring) - this.currentMrrCentsValue;
    const freeRecurringCents = monthlyCents * freeRecurring;
    const prepaidCents       = monthlyCents * this.prepaidCountValue;
    const creditPackCents    = monthlyCents * this.creditPackCountValue;
    const uncategorizedCents = monthlyCents * this.uncategorizedCountValue;

    const recurringOn      = cohortOn("recurring");
    const freeRecurringOn  = cohortOn("freeRecurring");
    const prepaidOn        = cohortOn("prepaid");
    const creditPackOn     = cohortOn("creditPack");
    const uncategorizedOn  = cohortOn("uncategorized");

    const aggregateDelta =
      (recurringOn     ? recurringDelta     : 0) +
      (freeRecurringOn ? freeRecurringCents : 0) +
      (prepaidOn       ? prepaidCents       : 0) +
      (creditPackOn    ? creditPackCents    : 0) +
      (uncategorizedOn ? uncategorizedCents : 0);

    const grandTotalCents = this.currentMrrCentsValue + aggregateDelta;

    // Top cards: New MRR (signed delta) + Total MRR (absolute new total).
    this.setText(this.potentialTotalTarget, monthlyCents > 0 ? this.formatSigned(aggregateDelta) + "/mo" : "—");
    if (this.hasTotalMrrTarget) {
      this.setText(this.totalMrrTarget, monthlyCents > 0 ? this.formatCents(grandTotalCents) + "/mo" : "—");
    }

    // Cohort table rows — each cohort's signed contribution. When a row's
    // toggle is OFF we show "(excluded)" so the operator sees the value
    // they're leaving on the table without it actually counting.
    const renderCohort = (target, cents, on, signed = true) => {
      if (!target) return;
      if (!monthlyCents) { this.setText(target, "—"); return; }
      const formatted = signed ? this.formatSigned(cents) : this.formatCents(cents);
      this.setText(target, on ? formatted : `(${this.formatCents(Math.abs(cents))} excluded)`);
    };
    renderCohort(this.hasRecurringTotalTarget && this.recurringTotalTarget, recurringDelta, recurringOn);
    renderCohort(this.hasFreeRecurringTotalTarget && this.freeRecurringTotalTarget, freeRecurringCents, freeRecurringOn);
    renderCohort(this.hasPrepaidTotalTarget && this.prepaidTotalTarget, prepaidCents, prepaidOn);
    renderCohort(this.hasCreditPackTotalTarget && this.creditPackTotalTarget, creditPackCents, creditPackOn);
    renderCohort(this.hasUncategorizedTotalTarget && this.uncategorizedTotalTarget, uncategorizedCents, uncategorizedOn);

    this.setText(this.grandTotalTarget, monthlyCents > 0 ? this.formatCents(grandTotalCents) : "—");
  }

  // Convert override price + interval into a monthly-equivalent cents amount.
  // Returns 0 when the operator hasn't entered anything yet, so the UI stays
  // in its initial "—" state instead of pretending zero is a real answer.
  computeMonthlyCents() {
    const priceDollars = parseFloat(this.overridePriceTarget?.value || "");
    if (isNaN(priceDollars) || priceDollars <= 0) return 0;

    const intervalType = this.overrideIntervalTarget?.value || "month";
    const priceCents = Math.round(priceDollars * 100);

    switch (intervalType) {
      case "day":   return Math.round(priceCents * (365 / 12));
      case "week":  return Math.round(priceCents * (52 / 12));
      case "month": return priceCents;
      default:      return 0;
    }
  }

  setText(el, text) {
    if (!el) return;
    el.textContent = text;
  }

  formatCents(cents) {
    const dollars = (cents / 100);
    return dollars.toLocaleString("en-US", { style: "currency", currency: "USD" });
  }

  // Signed currency: explicit + for positive, native − sign for negative.
  // Used on delta lines so an operator can tell at a glance whether the
  // picked plan would help or hurt MRR.
  formatSigned(cents) {
    const formatted = this.formatCents(Math.abs(cents));
    if (cents > 0) return `+${formatted}`;
    if (cents < 0) return `−${formatted}`;
    return formatted;
  }
}
